import { useMediaQuery } from '@mui/material';
import { UI_KIND, uiTargetAttrs } from '@/common/uiEventNames';
import { FavoriteClinicBadge } from '@/components/domain/recruitment/RecruitmentGrid';
import { RECRUITMENT_CHIP_HEIGHT, recruitmentChipStyle, recruitmentChipCountStyle, recruitmentChipRowStyle, recruitmentChipAccentStyle } from '@/components/domain/recruitment/recruitmentChipStyles';
import { stationChip, recruitmentChips, accentChipOf, pinnedChipsOf } from '@/components/domain/recruitment/recruitmentChips';
import OneLineTags from '@/components/domain/recruitment/OneLineTags';
import styled from '@emotion/styled';
import { RecruitmentTableRow } from '@/pages/Home/components/RecruitmentTable';
import { WorkDaysLine, keepTildeTogether, summaryText } from '@/components/domain/recruitment/recruitmentSummary';
import FitOneLine from '@/components/common/FitOneLine';
import RecruitmentInlineBadge from '@/components/domain/recruitment/RecruitmentInlineBadge';

// 지역 카드: 치과명·조회·관심 → 제목 → 근무 요일 → 지역·업무·근무형태.
const List = styled.div`
    display: flex;
    flex-direction: column;
    margin-top: 0;
    border-top: 1px solid #eef0f3;
`;

const RowItem = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto auto;
    align-items: center;
    gap: 6px 12px;
    padding: 10px 8px;
    border-bottom: 1px solid #eef0f3;
    cursor: pointer;
    transition: background-color 0.15s ease;

    &:hover {
        background: #f8f9fa;
    }

    @media (max-width: 768px) {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        justify-content: flex-start;
        gap: 3px;
        padding: 6px 8px;
        /* 제목이 한 줄이면 그만큼 줄여, 내용 아래 빈 행을 예약하지 않는다. */
    }
`;

const RowHeader = styled.div`
    grid-column: 1 / -1;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    width: 100%;
    min-width: 0;
`;

const NameGroup = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    flex: 1;
    min-width: 0;
`;

const ClinicName = styled.span`
    font-size: 0.85rem;
    color: #6b7280;
    font-weight: 600;
    min-width: 0;
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    @media (max-width: 768px) {
        font-size: 12px;
    }
`;

const Title = styled.div`
    flex: 1;
    min-width: 0;
    font-size: 0.92rem;
    font-weight: 700;
    color: #1f2937;
    line-height: 1.3;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    max-height: 2.6em;
    word-break: keep-all;
    overflow-wrap: anywhere;

    /* PC 한 줄 목록 — 제목도 한 줄(넘치면 말줄임)이라 행 높이가 같다 */
    @media (min-width: 769px) {
        -webkit-line-clamp: 1;
        max-height: 1.3em;
    }

    @media (max-width: 768px) {
        width: 100%;
        flex: none;
        flex-shrink: 0;
    }
`;

const Meta = styled(FitOneLine)`
    font-size: 0.78rem;
    /* 줄 높이는 기본 크기 기준 고정 — 글자가 줄어도 행 높이 불변 */
    line-height: calc(0.78rem * 1.4);
    color: #9aa0a6;
    flex-shrink: 0;

    @media (max-width: 768px) {
        width: 100%;
        height: calc(0.78rem * 1.4);
        color: #6b7280;
    }
`;

/** 근무 요일 — PC 한 줄에서는 줄이지 않고(제목이 줄어든다, 칸이 글 폭 그대로라 축소도 없음), 폰에서는 제목 아래 한 줄 */
const RowWorkDays = styled(WorkDaysLine)`
    @media (min-width: 769px) {
        flex-shrink: 0;
    }

    @media (max-width: 768px) {
        width: 100%;
    }
`;

const Counts = styled.span`
    font-size: 12px;
    color: #9aa0a6;
    flex-shrink: 0;
    white-space: nowrap;
    @media (max-width: 768px) {
        font-size: 10px;
        line-height: 1.4;
    }
`;

interface Props {
    list: RecruitmentTableRow[];
    onClick: (id: number) => void;
}

const RecruitmentRowList = ({ list, onClick }: Props) => {
    const isMobile = useMediaQuery('(max-width:768px)');
    return (
        <List>
            {list.map((r) => {
                return (
                    <RowItem key={r.id} data-card="row" {...uiTargetAttrs(UI_KIND.RECRUITMENT, r.id)} onClick={() => onClick(r.id)}>
                        <RowHeader>
                            {/* 썸네일이 없는 행 — 채용 카드와 같은 배지(급구·신규·상시모집·종료임박·모집종료)를 병원명 옆에 */}
                            <NameGroup>
                                <ClinicName style={{ flex: '0 1 auto' }}>{r.clinic.name}</ClinicName>
                                <RecruitmentInlineBadge info={r} />
                                {r.isFavoriteClinic && <span aria-label="관심치과" style={{ position: 'relative', width: isMobile ? 26 : 88, height: 24, flexShrink: 0 }}><FavoriteClinicBadge show isMobile={isMobile} /></span>}
                            </NameGroup>
                            <Counts>조회 {(r.viewCount ?? 0).toLocaleString()} · 관심 {(r.favoriteCount ?? 0).toLocaleString()}</Counts>
                        </RowHeader>
                        <Title>{keepTildeTogether(r.title)}</Title>
                        {r.workDaysLabel?.trim() && <RowWorkDays size="0.82rem" label={r.workDaysLabel.trim()} />}
                        {/* 칩 줄 — 채용 카드와 같은 규칙(역 → 복지, 복지가 없으면 급여·경력·직급), 한 줄에 안 들어가면 +N */}
                        {recruitmentChips(r.clinic, r.welfares, r.infoChips, r.sameDayPay, r.payChip).length > 0 && (
                            <OneLineTags
                                heightPx={RECRUITMENT_CHIP_HEIGHT}
                                tags={recruitmentChips(r.clinic, r.welfares, r.infoChips, r.sameDayPay, r.payChip)}
                                keepFirst={!!stationChip(r.clinic)}
                                pinned={pinnedChipsOf(r.sameDayPay, r.payChip, r.infoChips)}
                                gapPx={4}
                                style={{ ...recruitmentChipRowStyle, gridColumn: '1 / -1', order: 1, width: '100%', minWidth: 0 }}
                                tagStyle={recruitmentChipStyle}
                                accentTag={accentChipOf(r.sameDayPay)}
                                accentStyle={recruitmentChipAccentStyle}
                                plusStyle={recruitmentChipCountStyle}
                            />
                        )}
                        <Meta line="summary" text={summaryText(r.clinic.city, r.clinic.district, r.jobTasks, r.recruitmentTypes, true)} />
                    </RowItem>
                );
            })}
        </List>
    );
};

export default RecruitmentRowList;
